import React, { useState } from 'react';
import {
  AgxLabel,
  AgxDivider,
  AgxColumn,
  AgxRow,
  Images,
  AgxBodyText,
  UploadedDocument,
  DocumentTypes,
} from '@urbanx/agx-ui-components';
import { isEqual } from 'lodash-es';
import { useAppSelector } from 'hooks/useAppSelector';
import FileUpload from '../FileUpload/FileUpload';
import './TypedDocumentUpload.scss';
import { useNewSelectedContract } from 'hooks/useNewSelectedContract';
import { FormType } from '@urbanx/agx.campaigns.client';

interface TypedDocumentUploadProps {
  id: string;
  title?: string;
  documentConfig: Array<{
    documentType: DocumentTypes;
    description: string;
    required?: boolean;
    readonly?: boolean;
    multipleFiles?: boolean;
    displayDocuments?: UploadedDocument[] | null;
  }>;
  defaultValue?: UploadedDocument[];
  onValueChanged: (value: { id: string; value?: UploadedDocument[] }) => void;
  validate?: boolean;
}

const TypedDocumentUpload = ({
  id,
  title,
  documentConfig,
  defaultValue,
  onValueChanged,
  validate,
}: TypedDocumentUploadProps) => {
  const { campaignId, formId, selectedForm } = useAppSelector((state) => state.form);
  const contract = useNewSelectedContract();
  const contractId =
    contract?.contractId ??
    (selectedForm?.formType === FormType.ContractSubmission ? formId : undefined);
  const documents = defaultValue ?? [];
  const [uploadError, setUploadError] = useState<string | null>(null);
  const updateDocumentDetail = (
    documentType: DocumentTypes,
    updatedDocumentDetails?: UploadedDocument[]
  ) => {
    const duplicateDocuments = documents.filter(
      (d) =>
        d.documentType !== documentType &&
        updatedDocumentDetails?.some((u) => u.containerFilePath === d.containerFilePath)
    );

    if (duplicateDocuments.length > 0) {
      setUploadError('Cannot overwrite an existing document. Try use a different file name.');
      return;
    }

    setUploadError(null);

    const updatedDocuments = documents
      .filter((d) => d.documentType !== documentType)
      .concat(updatedDocumentDetails ?? []);

    const sortByFileName = (docs: UploadedDocument[]) =>
      [...docs].sort((a, b) => a.fileName.localeCompare(b.fileName));

    const sortedUpdatedDocuments = sortByFileName(updatedDocuments);
    const sortedDocuments = sortByFileName(documents);

    if (!isEqual(sortedUpdatedDocuments, sortedDocuments)) {
      onValueChanged({ id, value: updatedDocuments });
    }
  };

  return (
    <AgxColumn extraLargeGap>
      {title && <AgxLabel large>{title}</AgxLabel>}
      {documentConfig.map((file, index) => {
        const { documentType, description, required, readonly, multipleFiles, displayDocuments } =
          file;

        const matchingDocuments =
          displayDocuments ?? documents.filter((doc) => doc.documentType === documentType);

        return (
          <React.Fragment key={index}>
            {index !== 0 ? <AgxDivider expanded /> : <></>}
            <FileUpload
              key={index}
              id={index.toString()}
              title={description}
              extensions={['pdf']}
              required={required}
              validate={validate}
              readonly={displayDocuments != null || readonly}
              fileData={matchingDocuments}
              campaignId={campaignId}
              contractId={contractId}
              documentType={documentType}
              multiple={multipleFiles}
              onValueChanged={({ value }) => {
                if (displayDocuments == null) updateDocumentDetail(documentType, value);
              }}
            />
          </React.Fragment>
        );
      })}
      {uploadError && (
        <AgxRow fill mediumGap>
          <span>
            <Images.AlertCircle />
          </span>
          <AgxBodyText small>{uploadError}</AgxBodyText>
        </AgxRow>
      )}
    </AgxColumn>
  );
};

export default TypedDocumentUpload;
